import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import MultiSelect from "@/v2-ui/multiSelect"
import Button from "@/v2-backoffice-client/components/button"
import { LICENSE_STATUS_NONE } from "@/v2-backoffice-client/license/license.constants"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import {
  CHURN_RISK_LABELS,
  type ChurnRiskLevel,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  SCORE_LEVEL_LABELS,
  type ScoreLevel,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  CHURN_FILTER_LEVELS,
  SCORE_FILTER_LEVELS,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import {
  hasActiveFilters,
  type PortfolioFilters,
} from "@/v2-backoffice-client/portfolio/portfolio.filters"
import type { PortfolioCustomer } from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioFilterBarProps = ComponentProps<"div"> & {
  /** UNFILTERED customer groups — the option lists must not shrink as you filter. */
  customers: PortfolioCustomer[]
  filters: PortfolioFilters
  /** What the current scope resolves to, shown so filters have visible consequence. */
  scopedCustomerCount: number
  scopedSeatCount: number
  onCustomerIdsChange(ids: string[]): void
  onExcludeCustomerIdsChange(ids: string[]): void
  onExcludeUserIdsChange(ids: number[]): void
  onOwnersChange(owners: string[]): void
  onChurnLevelsChange(levels: ChurnRiskLevel[]): void
  onScoreLevelsChange(levels: ScoreLevel[]): void
  onLicenseStatusCodesChange(codes: number[]): void
  onClear(): void
}

// The Portfolio's global filter bar: WHO is in scope (customer
// include/exclude, user exclude, churn risk, activity level, license
// status) — applied across every sub-tab. "Only my trials" is the license
// facet set to Trial: every chart, segment and play then reads on trial
// customers alone. Time selection deliberately lives on the individual
// time-series sections instead: everything else is computed upfront in
// Databricks on a fixed 28-day window and cannot re-window.
function PortfolioFilterBar(props: PortfolioFilterBarProps) {
  const {
    customers,
    filters,
    scopedCustomerCount,
    scopedSeatCount,
    onCustomerIdsChange,
    onExcludeCustomerIdsChange,
    onExcludeUserIdsChange,
    onOwnersChange,
    onChurnLevelsChange,
    onScoreLevelsChange,
    onLicenseStatusCodesChange,
    onClear,
    className,
    ...divProps
  } = props

  // Status names come from the same lookup the licenses board uses, so the
  // facet labels always match the badges; "No license" is the app-side
  // sentinel (0) for customers holding nothing.
  const { data: statusesData } = useLicenseStatusesQuery()
  const licenseStatusOptions = [
    ...(statusesData?.getLicenseStatuses ?? []).map((status) => ({
      value: String(status.Code),
      label: status.Name,
    })),
    { value: String(LICENSE_STATUS_NONE), label: "No license" },
  ]

  const customerOptions = [...customers]
    .sort((a, b) => a.Name.localeCompare(b.Name))
    .map((customer) => ({
      value: customer.ExternalCustomerId,
      label: customer.Name,
    }))

  const userOptions = customers
    .flatMap((customer) =>
      customer.Members.map((member) => ({
        value: String(member.UserId),
        label: `${
          member.FirstName || member.LastName
            ? formatUserName(member.FirstName, member.LastName)
            : (member.Email ?? `User #${member.UserId}`)
        } · ${customer.Name}`,
      })),
    )
    .sort((a, b) => a.label.localeCompare(b.label))

  // The book's distinct CRM owners (HubSpot company owner) — "my accounts"
  // for a CSM/sales person is this facet set to their own name.
  const ownerOptions = [
    ...new Set(
      customers
        .map((customer) => customer.Owner)
        .filter((owner): owner is string => !!owner),
    ),
  ]
    .sort((a, b) => a.localeCompare(b))
    .map((owner) => ({ value: owner, label: owner }))

  return (
    <div
      className={cn("flex flex-wrap items-center gap-2", className)}
      {...divProps}
    >
      {/* Fixed-width WRAPPERS, not classNames on the triggers: the trigger
          base class is w-full and cva doesn't tailwind-merge, so a width
          on the trigger loses the conflict and every control renders as a
          full-width stacked row. */}
      <div className="w-52 shrink-0">
        <MultiSelect
          size="xs"
          value={filters.customerIds}
          onChange={onCustomerIdsChange}
          options={customerOptions}
          placeholder="All customers"
          searchPlaceholder="Search customers…"
        />
      </div>

      <div className="w-52 shrink-0">
        <MultiSelect
          size="xs"
          value={filters.excludeCustomerIds}
          onChange={onExcludeCustomerIdsChange}
          options={customerOptions}
          placeholder="Exclude customers"
          searchPlaceholder="Search customers…"
        />
      </div>

      <div className="w-52 shrink-0">
        <MultiSelect
          size="xs"
          value={filters.excludeUserIds.map(String)}
          onChange={(values) => onExcludeUserIdsChange(values.map(Number))}
          options={userOptions}
          placeholder="Exclude users"
          searchPlaceholder="Search users…"
        />
      </div>

      {/* Only when the CRM sync has owners to offer — a dead facet teaches
          people to ignore the bar. */}
      {ownerOptions.length > 0 && (
        <div className="w-44 shrink-0">
          <MultiSelect
            size="xs"
            value={filters.owners}
            onChange={onOwnersChange}
            options={ownerOptions}
            placeholder="Company owner"
            searchPlaceholder="Search owners…"
          />
        </div>
      )}

      <div className="w-40 shrink-0">
        <MultiSelect
          size="xs"
          value={filters.churnLevels}
          onChange={(values) => onChurnLevelsChange(values as ChurnRiskLevel[])}
          options={CHURN_FILTER_LEVELS.map((level) => ({
            value: level,
            label: CHURN_RISK_LABELS[level],
          }))}
          placeholder="Churn risk"
        />
      </div>

      <div className="w-40 shrink-0">
        <MultiSelect
          size="xs"
          value={filters.scoreLevels}
          onChange={(values) => onScoreLevelsChange(values as ScoreLevel[])}
          options={SCORE_FILTER_LEVELS.map((level) => ({
            value: level,
            label: SCORE_LEVEL_LABELS[level],
          }))}
          placeholder="Activity level"
        />
      </div>

      <div className="w-40 shrink-0">
        <MultiSelect
          size="xs"
          value={filters.licenseStatusCodes.map(String)}
          onChange={(values) => onLicenseStatusCodesChange(values.map(Number))}
          options={licenseStatusOptions}
          placeholder="License status"
        />
      </div>

      {hasActiveFilters(filters) && (
        <Button
          variant="ghost"
          size="xs"
          iconSrc="cross-small"
          onClick={onClear}
        >
          Clear
        </Button>
      )}

      <span className="ml-auto text-xs tabular-nums text-muted-foreground">
        {formatNumber(scopedCustomerCount)} customer
        {scopedCustomerCount === 1 ? "" : "s"} · {formatNumber(scopedSeatCount)}{" "}
        seats in scope
      </span>
    </div>
  )
}

export default PortfolioFilterBar
